import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import { useDebounce } from '@/shared/hooks';
import { LoadingSpinner, EmptyState } from '@/shared/components';
import { useRetailersQuery } from '../hooks/useRetailersQuery';
import RetailersTable from './RetailersTable';
import RetailerFormDialog from './RetailerFormDialog';
import RetailersImportDialog from './RetailersImportDialog';
import { retailersService } from '../services/retailers.service';

type ViewMode = 'table' | 'edit';

export default function RetailersContainer() {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [editingRetailerId, setEditingRetailerId] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 500);

  const { data, isLoading, error } = useRetailersQuery({
    page,
    limit: 20,
    search: debouncedSearch || undefined,
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleExport = async () => {
    try {
      const blob = await retailersService.exportExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'retailers.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleEdit = (retailerId: string) => {
    setEditingRetailerId(retailerId);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingRetailerId(null);
  };

  if (error) {
    return (
      <EmptyState
        title="Error al cargar retailers"
        description="No se pudieron cargar los retailers. Intenta nuevamente."
      />
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={viewMode}
            onChange={(_, v) => setViewMode(v)}
          >
            <Tab label="Vista Tabla" value="table" />
            <Tab label="Vista Edición" value="edit" />
          </Tabs>
        </Box>

        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 3,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <TextField
            placeholder="Buscar retailer..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{ flex: 1, minWidth: 200, maxWidth: 400 }}
          />
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setIsImportOpen(true)}
            >
              Importar
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
            >
              Exportar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsFormOpen(true)}
            >
              Nuevo Retailer
            </Button>
          </Box>
        </Box>

        {/* Content */}
        {isLoading ? (
          <LoadingSpinner message="Cargando retailers..." />
        ) : !data?.retailers.length ? (
          <EmptyState
            title="No hay retailers"
            description={
              search
                ? 'No se encontraron retailers que coincidan con la búsqueda.'
                : 'Aún no hay retailers registrados.'
            }
            action={{
              label: 'Agregar Retailer',
              onClick: () => setIsFormOpen(true),
            }}
          />
        ) : (
          <RetailersTable
            retailers={data.retailers}
            page={data.page}
            totalPages={data.totalPages}
            onPageChange={setPage}
            onEdit={handleEdit}
            viewMode={viewMode}
          />
        )}

        {/* Dialogs */}
        <RetailerFormDialog
          open={isFormOpen}
          onClose={handleCloseForm}
          retailerId={editingRetailerId}
        />

        <RetailersImportDialog
          open={isImportOpen}
          onClose={() => setIsImportOpen(false)}
        />
      </CardContent>
    </Card>
  );
}
